"use client";

import { waitForTransactionReceipt, type WriteContractParameters } from "@wagmi/core";
import { type Hex, type TransactionReceipt } from "viem";
import { useConfig } from "wagmi";

import { type BatchCall } from "./contracts";
import { usePrivySignerTxs } from "./privy";
import { type TxStep } from "./useTxSteps";

interface HandleBatchTxParams {
  title: string;
  calls: BatchCall[];
  chainId: number;
  updateStep: (updates: Partial<TxStep>) => void;
  afterSigning?: (txHash: Hex | undefined) => Promise<void> | void;
  afterConfirming?: (receipt: TransactionReceipt | undefined) => Promise<void> | void;
}

/**
 * Batch counterpart of useHandleTx: submits all calls atomically in a single
 * wallet_sendCalls transaction (Privy signer) and drives one TxStep through
 * sending -> confirming -> confirmed.
 */
export function useHandleBatchTx() {
  const config = useConfig();
  const { sendBatchTransaction } = usePrivySignerTxs();

  return async function handleBatchTx(params: HandleBatchTxParams): Promise<void> {
    const { calls, chainId, updateStep, afterSigning, afterConfirming } = params;

    if (!calls.length) return;

    let hash: Hex;
    try {
      updateStep({ status: "sending", error: undefined });

      hash = await sendBatchTransaction(
        calls.map(
          (call) =>
            ({
              address: call.address,
              abi: call.abi,
              functionName: call.functionName,
              args: call.args,
              value: call.value,
              chainId,
            }) as WriteContractParameters,
        ),
      );

      updateStep({ txHash: hash });

      if (afterSigning) await afterSigning(hash);
    } catch (error) {
      if (afterSigning) await afterSigning(undefined);
      updateStep({ status: "idle", error });
      return;
    }

    try {
      updateStep({ status: "confirming", error: undefined });

      const receipt = await waitForTransactionReceipt(config, {
        hash,
        chainId: chainId as (typeof config)["chains"][number]["id"],
      });
      if (afterConfirming) await afterConfirming(receipt);

      updateStep({ status: "confirmed", error: undefined });
    } catch (error) {
      if (afterConfirming) await afterConfirming(undefined);
      updateStep({ status: "idle", error });
    }
  };
}
